/**
 * StripeSecretsService
 * 
 * Service frontend pour la gestion sécurisée des secrets Stripe.
 * Communique avec le backend pour stocker les secrets dans Google Cloud Secret Manager.
 * 
 * IMPORTANT: Les secrets ne sont JAMAIS stockés côté client ou dans Firestore directement.
 * Seules les versions masquées sont récupérées pour l'affichage.
 */


import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';

// Note: On utilise fetch directement plutôt que les httpsCallable de Firebase

export interface SaveStripeSecretsRequest {
    organizationId: string;
    secretKey: string;
    publishableKey: string;
    paypalEnabled?: boolean;
}

export interface SaveStripeSecretsResponse {
    success: boolean;
    maskedSecretKey?: string;
    maskedPublishableKey?: string;
    maskedWebhookSecret?: string;
    webhookConfigured?: boolean;
    accountName?: string;
    isTestMode?: boolean;
    error?: string;
}

export interface StripeSecretsMetadata {
    hasSecretKey: boolean;
    hasPublishableKey: boolean;
    hasWebhookSecret: boolean;
    secretKeyMasked: string | null;
    publishableKeyMasked: string | null;
    webhookSecretMasked: string | null;
    accountName: string | null;
    isTestMode: boolean;
    configuredAt: string | null;
    webhookConfiguredAt: string | null;
}

const API_BASE_URL = FUNCTIONS_BASE_URL;

class StripeSecretsServiceClass {
    /**
     * Sauvegarde les clés Stripe de manière sécurisée
     * Les clés sont envoyées au backend qui les stocke dans Secret Manager
     * 
     * @returns Métadonnées avec versions masquées (pas les vraies clés)
     */
    async saveSecrets(request: SaveStripeSecretsRequest): Promise<SaveStripeSecretsResponse> {
        try {
            console.log(`🔐 [StripeSecrets] Saving secrets for org: ${request.organizationId}`);

            const response = await fetch(`${API_BASE_URL}/saveStripeSecrets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la sauvegarde des secrets');
            }

            const result = await response.json();
            console.log(`✅ [StripeSecrets] Saved successfully:`, {
                maskedSecretKey: result.maskedSecretKey,
                webhookConfigured: result.webhookConfigured,
            });

            return result;
        } catch (error: any) {
            console.error(`❌ [StripeSecrets] Error saving secrets:`, error);
            throw error;
        }
    }

    /**
     * Récupère les métadonnées des secrets (sans les valeurs réelles)
     * Utilisé pour afficher les versions masquées dans l'UI
     */
    async getMetadata(organizationId: string): Promise<StripeSecretsMetadata | null> {
        try {
            const response = await fetch(
                `${API_BASE_URL}/getStripeSecretsMetadata?organizationId=${encodeURIComponent(organizationId)}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.status === 404) {
                return null;
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la récupération des métadonnées');
            }

            return await response.json();
        } catch (error: any) {
            console.error(`❌ [StripeSecrets] Error getting metadata:`, error);
            throw error;
        }
    }

    /**
     * Génère une version masquée d'une clé (côté client pour preview avant envoi)
     * Note: Le masquage "officiel" est fait côté serveur
     */
    maskKey(value: string): string {
        if (!value || value.length < 12) {
            return value ? '***' : '';
        }

        // Garder le préfixe (sk_test_, pk_live_, whsec_, etc.)
        const prefixMatch = value.match(/^([a-z_]+_)/i);
        const prefix = prefixMatch ? prefixMatch[1] : '';

        // Caractères de début après le préfixe
        const startChars = prefix.length > 0 ? value.substring(prefix.length, prefix.length + 4) : value.substring(0, 4);

        // Derniers caractères
        const endChars = value.slice(-5);

        return `${prefix}${startChars}...${endChars}`;
    }
}

export const StripeSecretsService = new StripeSecretsServiceClass();
